import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LEAD_TYPES, type Paginated } from '@insurensa/shared';
import {
  Badge,
  Card,
  DataTable,
  PageTitle,
  Property,
  PropertyList,
  type DataTableColumn,
} from '@insurensa/ui';
import { useFormat, useT, type BoundFormat, type Translate } from '@insurensa/i18n';
import { api, qs } from '../lib/api.js';
import { BackLink } from '../components/Layout.js';
import { BlockSkeleton, ErrorAlert } from '../components/ui.js';

interface LeadRow {
  id: string;
  reference: string;
  type: string;
  typeLabel: string;
  city: string | null;
  postalCode: string | null;
  offerCount: number;
  assignedPartners: number;
  createdAt: string;
  user: { id: string; email: string | null };
}

/**
 * One screen replacing the six near-identical listings — kfzLeads.php,
 * phvLeads.php, pkvLeads.php, gkvLeads.php, bsvLeads.php and rsLeads.php — that
 * differed only in the table name in their SQL.
 */
export function LeadsPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const type = searchParams.get('type') ?? '';
  const search = searchParams.get('search') ?? '';

  const { data, error, isLoading } = useQuery({
    queryKey: ['leads', type, search, page, pageSize],
    queryFn: () => api.get<Paginated<LeadRow>>(`/admin/leads${qs({ type, search, page, pageSize })}`),
  });

  const setParam = (key: string, value: string): void => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
    setPage(1);
  };

  const columns: Array<DataTableColumn<LeadRow>> = [
    {
      key: 'reference',
      header: t('common.reference'),
      className: 'nowrap',
      value: (row) => row.reference.slice(-10),
      render: (row) => <Link to={`/leads/${row.id}`}>{row.reference.slice(-10)}</Link>,
    },
    {
      key: 'type',
      header: t('fields.insuranceLine'),
      value: (row) => fmt.leadType(row.type),
      render: (row) => (
        <Badge tone="primary">
          <span title={fmt.leadType(row.type)}>{row.type}</span>
        </Badge>
      ),
    },
    { key: 'city', header: t('fields.city'), value: (row) => row.city ?? fmt.empty },
    { key: 'postalCode', header: t('fields.postalCodeShort'), value: (row) => row.postalCode ?? fmt.empty },
    {
      key: 'user',
      header: t('fields.user'),
      className: 'wrap-anywhere',
      // Guests submit without an account, so there is no address to show.
      value: (row) => row.user.email ?? t('admin.leads.guest'),
      render: (row) =>
        row.user.email ?? <em className="text-muted">{t('admin.leads.guest')}</em>,
    },
    {
      key: 'assignedPartners',
      header: t('fields.partner'),
      numeric: true,
      value: (row) => String(row.assignedPartners),
    },
    { key: 'offerCount', header: t('fields.offers'), numeric: true, value: (row) => String(row.offerCount) },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      className: 'nowrap',
      value: (row) => fmt.dateTime(row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      render: (row) => (
        <Link to={`/leads/${row.id}`} className="btn btn-primary btn-sm">
          {t('common.details')}
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        heading={t('admin.nav.leads')}
        subheading={t('admin.leads.subheading')}
        icon="pe-7s-note2"
      />

      <ErrorAlert error={error} />

      <Card noPadding>
        <div className="card-body">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            exportFileName="leads"
            exportTitle={
              type
                ? t('admin.leads.exportTitleFiltered', { line: fmt.leadType(type) })
                : t('admin.leads.exportTitle')
            }
            emptyMessage={t('admin.leads.empty')}
            search={{ value: search, onChange: (value) => setParam('search', value) }}
            pagination={{
              page: data?.page ?? page,
              pageSize: data?.pageSize ?? pageSize,
              total: data?.total ?? 0,
              totalPages: data?.totalPages ?? 1,
              onPage: setPage,
              onPageSize: (next) => {
                setPageSize(next);
                setPage(1);
              },
            }}
            onExportAll={async () => {
              const all = await api.get<Paginated<LeadRow>>(
                `/admin/leads${qs({ type, search, page: 1, pageSize: 100 })}`,
              );
              return all.items;
            }}
            toolbar={
              <div className="form-inline mt-2">
                <label className="mr-2 mb-0 small text-muted" htmlFor="type">
                  {t('fields.insuranceLine')}
                </label>
                <select
                  id="type"
                  className="form-control form-control-sm"
                  value={type}
                  onChange={(event) => setParam('type', event.target.value)}
                >
                  <option value="">{t('common.all')}</option>
                  {LEAD_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {fmt.leadType(value)}
                    </option>
                  ))}
                </select>
              </div>
            }
          />
        </div>
      </Card>
    </>
  );
}

interface LeadDetail {
  id: string;
  reference: string;
  type: string;
  typeLabel: string;
  city: string | null;
  postalCode: string | null;
  state: string | null;
  district: string | null;
  distanceKm: number | null;
  createdAt: string;
  contactPhone: string | null;
  detail: Record<string, unknown> | null;
  documents: Array<{ id: string; url: string; name: string; mimeType: string }>;
  user: { id: string; email: string | null };
  offers: Array<{
    id: string;
    amountCents: number;
    paymentFrequency: string;
    status: string;
    createdAt: string;
    partner: { id: string; companyName: string };
  }>;
  assignments: Array<{
    partner: { id: string; companyName: string };
    distanceKm: number | null;
    viewedAt: string | null;
  }>;
}

/** Replaces viewKFZDetails.php and its five siblings. */
export function LeadDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const fmt = useFormat();

  const { data, error, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.get<LeadDetail>(`/admin/leads/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <>
        <PageTitle heading={t('admin.leadDetail.heading')} icon="pe-7s-note2" />
        <BlockSkeleton height={220} />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageTitle heading={t('admin.leadDetail.heading')} icon="pe-7s-note2" />
        <ErrorAlert error={error} />
      </>
    );
  }

  return (
    <>
      <PageTitle
        heading={fmt.leadType(data.type)}
        subheading={t('common.referenceOf', { reference: data.reference })}
        icon="pe-7s-note2"
        actions={<BackLink to="/leads">{t('common.back')}</BackLink>}
      />

      <div className="row">
        <div className="col-lg-8">
          <Card
            title={<div className="card-header-title">{t('admin.leadDetail.customerAnswers')}</div>}
            noPadding
          >
            <AnswerTable detail={data.detail} />
          </Card>

          {data.documents.length > 0 && (
            <Card
              title={
                <div className="card-header-title">{t('admin.leadDetail.uploadedDocuments')}</div>
              }
            >
              <ul className="mb-0 pl-3">
                {data.documents.map((document) => (
                  <li key={document.id}>
                    <a href={document.url} target="_blank" rel="noreferrer noopener">
                      {document.name}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card
            title={
              <div className="card-header-title">
                {t('admin.leadDetail.offersWithCount', { count: data.offers.length })}
              </div>
            }
            noPadding
          >
            {data.offers.length === 0 ? (
              <div className="p-4 text-center text-muted">{t('admin.leadDetail.noOffers')}</div>
            ) : (
              <table className="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('fields.partner')}</th>
                    <th className="text-right">{t('fields.amount')}</th>
                    <th>{t('fields.paymentFrequency')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('fields.sentAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.offers.map((offer) => (
                    <tr key={offer.id}>
                      <td>
                        <Link to={`/partners/${offer.partner.id}`}>{offer.partner.companyName}</Link>
                      </td>
                      <td className="text-right">{fmt.money(offer.amountCents)}</td>
                      <td>{fmt.frequency(offer.paymentFrequency)}</td>
                      <td>
                        <Badge value={offer.status}>{fmt.offer(offer.status)}</Badge>
                      </td>
                      <td>{fmt.dateTime(offer.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title={<div className="card-header-title">{t('admin.leadDetail.overview')}</div>}>
            <PropertyList>
              <tbody>
                <Property label={t('fields.insuranceLine')}>{fmt.leadType(data.type)}</Property>
                <Property label={t('fields.user')}>
                  <Link to={`/users/${data.user.id}`}>{data.user.email}</Link>
                </Property>
                <Property label={t('fields.city')}>{data.city ?? fmt.empty}</Property>
                <Property label={t('fields.postalCode')}>{data.postalCode ?? fmt.empty}</Property>
                <Property label={t('fields.state')}>{data.state ?? fmt.empty}</Property>
                <Property label={t('admin.leadDetail.requestedRadius')}>
                  {data.distanceKm !== null
                    ? t('common.kilometres', { km: data.distanceKm })
                    : fmt.empty}
                </Property>
                <Property label={t('fields.phone')}>
                  {data.contactPhone ?? t('admin.leadDetail.phoneAfterAcceptance')}
                </Property>
                <Property label={t('common.createdAt')}>{fmt.dateTime(data.createdAt)}</Property>
              </tbody>
            </PropertyList>
          </Card>

          <Card
            title={
              <div className="card-header-title">
                {t('admin.leadDetail.assignedPartners', { count: data.assignments.length })}
              </div>
            }
            noPadding
          >
            {data.assignments.length === 0 ? (
              <div className="p-3 text-muted small">
                {t('admin.leadDetail.noAssignedPartners')}
              </div>
            ) : (
              <table className="table table-hover mb-0">
                <tbody>
                  {data.assignments.map((assignment) => (
                    <tr key={assignment.partner.id}>
                      <td>
                        <Link to={`/partners/${assignment.partner.id}`}>
                          {assignment.partner.companyName}
                        </Link>
                        <div className="small text-muted">
                          {assignment.viewedAt
                            ? t('admin.leadDetail.viewed')
                            : t('admin.leadDetail.notViewed')}
                        </div>
                      </td>
                      <td className="text-right text-muted small">
                        {assignment.distanceKm !== null
                          ? t('common.kilometres', { km: assignment.distanceKm })
                          : fmt.empty}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function AnswerTable({ detail }: { detail: Record<string, unknown> | null }): JSX.Element {
  const t = useT();
  const fmt = useFormat();

  if (!detail) return <div className="p-3 text-muted">{t('admin.leadDetail.noAnswers')}</div>;

  const entries = Object.entries(detail).filter(([, value]) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  if (entries.length === 0) {
    return <div className="p-3 text-muted">{t('admin.leadDetail.noAnswers')}</div>;
  }

  return (
    <table className="table table-striped table-hover mb-0">
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key}>
            <th style={{ width: '40%', fontWeight: 500 }}>{fmt.answer(key)}</th>
            <td>{renderValue(value, fmt, t)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Answers arrive as loosely typed JSON from the mobile app: booleans, scalars,
 * and lists of objects (additional drivers, insured people). Flatten each shape
 * to one readable line, translating the nested keys the same way.
 */
function renderValue(value: unknown, fmt: BoundFormat, t: Translate): string {
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        entry && typeof entry === 'object'
          ? Object.entries(entry as Record<string, unknown>)
              .filter(([key]) => key !== 'position')
              .map(([key, inner]) => `${fmt.answer(key)}: ${String(inner)}`)
              .join(', ')
          : String(entry),
      )
      .join(' · ');
  }
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
